/**
 * Réglages santé — CDC §4.2 (sources, consentements, export, suppression).
 *
 * Concentre les fonctions de souveraineté des données (§1.4 / §6.4) : gérer
 * ses connexions, ses consentements, exporter ou supprimer ses données, et
 * consulter le journal d'accès (transparence radicale §6.3).
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

import { santeApi } from '../api/santeApi'
import { getUrl } from '../../../utils/api'
import { HEALTH_ACCENT, Loading, SectionTitle, cardSx, darkFieldSx } from './shared'

const SOURCE_TYPES = [
  { value: 'healthkit', label: 'Apple HealthKit' },
  { value: 'withings', label: 'Withings' },
  { value: 'garmin', label: 'Garmin' },
  { value: 'fitbit', label: 'Fitbit' },
  { value: 'glucometer', label: 'Glucomètre connecté' },
  { value: 'tensiometer', label: 'Tensiomètre connecté' },
  { value: 'manual', label: 'Saisie manuelle' },
]

export default function SettingsView() {
  const [overview, setOverview] = useState(null)
  const [exports, setExports] = useState([])
  const [logs, setLogs] = useState([])
  const [deletion, setDeletion] = useState(null)
  const [profile, setProfile] = useState(null)
  const [newSource, setNewSource] = useState('healthkit')
  const [loading, setLoading] = useState(true)

  const refresh = async () => {
    const [ov, ex, lg, del, pr] = await Promise.all([
      santeApi.getOverview(),
      santeApi.exports.list(),
      santeApi.accessLogs.list('?limit=10'),
      santeApi.getAccountDeletion(),
      santeApi.getProfile(),
    ])
    setOverview(ov)
    setExports(ex)
    setLogs(lg)
    setDeletion(del && del.id ? del : null)
    setProfile(pr)
    setLoading(false)
  }

  useEffect(() => {
    refresh()
  }, [])

  const addSource = async () => {
    await santeApi.sources.create({
      source_type: newSource,
      status: 'connected',
      connected_at: new Date().toISOString(),
    })
    refresh()
  }

  const toggleConsent = async (consent) => {
    await santeApi.consents.update(consent.id, { granted: !consent.granted })
    refresh()
  }

  const toggleCoaching = async () => {
    const updated = await santeApi.updateProfile({
      proactive_coaching_enabled: !profile.proactive_coaching_enabled,
    })
    setProfile(updated)
  }

  const requestExport = async (format) => {
    await santeApi.requestExport(format)
    setExports(await santeApi.exports.list())
  }

  const requestDeletion = async () => {
    const d = await santeApi.requestAccountDeletion()
    setDeletion(d)
  }

  const cancelDeletion = async () => {
    await santeApi.cancelAccountDeletion()
    setDeletion(null)
  }

  if (loading) return <Loading label="Chargement des réglages…" />

  return (
    <Box>
      {/* Sources de données */}
      <Box sx={{ ...cardSx, mb: 2 }}>
        <SectionTitle>Sources de données</SectionTitle>
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          {(overview?.sources || []).map((s) => (
            <Box
              key={s.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: 'rgba(255,255,255,0.04)',
                borderRadius: 2,
                px: 2,
                py: 1,
              }}
            >
              <Typography sx={{ color: '#fff', fontSize: 14 }}>
                {s.source_label || s.source_type}
              </Typography>
              <Chip
                size="small"
                label={s.status}
                sx={{
                  bgcolor:
                    s.status === 'connected'
                      ? 'rgba(61,220,151,0.2)'
                      : 'rgba(255,255,255,0.08)',
                  color: s.status === 'connected' ? HEALTH_ACCENT : '#fff',
                }}
              />
            </Box>
          ))}
          {(overview?.sources || []).length === 0 && (
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
              Aucune source connectée.
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            select
            size="small"
            value={newSource}
            onChange={(e) => setNewSource(e.target.value)}
            sx={{ minWidth: 220, ...darkFieldSx }}
          >
            {SOURCE_TYPES.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            onClick={addSource}
            sx={{ color: HEALTH_ACCENT, textTransform: 'none' }}
          >
            Connecter
          </Button>
        </Stack>
      </Box>

      {/* Consentements */}
      <Box sx={{ ...cardSx, mb: 2 }}>
        <SectionTitle>Consentements</SectionTitle>
        {(overview?.consents || []).map((c) => (
          <Box
            key={c.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ color: '#fff', fontSize: 14 }}>
              {c.category_label || c.category}
            </Typography>
            <Switch
              checked={c.granted}
              onChange={() => toggleConsent(c)}
              sx={{
                '& .Mui-checked': { color: HEALTH_ACCENT },
                '& .Mui-checked + .MuiSwitch-track': { bgcolor: HEALTH_ACCENT },
              }}
            />
          </Box>
        ))}
        {(overview?.consents || []).length === 0 && (
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
            Aucun consentement enregistré.
          </Typography>
        )}
      </Box>

      {/* Coaching proactif */}
      {profile && (
        <Box
          sx={{
            ...cardSx,
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 700 }}>
              Coaching proactif
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
              Désactivable à tout moment, sans dégrader les autres fonctions.
            </Typography>
          </Box>
          <Switch
            checked={!!profile.proactive_coaching_enabled}
            onChange={toggleCoaching}
          />
        </Box>
      )}

      {/* Export RGPD */}
      <Box sx={{ ...cardSx, mb: 2 }}>
        <SectionTitle>Export de mes données</SectionTitle>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, mb: 1.5 }}>
          Droit à la portabilité (RGPD) : exportez l’intégralité de vos
          données santé à tout moment.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          {['json', 'pdf', 'full'].map((f) => (
            <Button
              key={f}
              variant="outlined"
              size="small"
              onClick={() => requestExport(f)}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}
            >
              {f.toUpperCase()}
            </Button>
          ))}
        </Stack>
        {exports.map((e) => (
          <Box
            key={e.id}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              py: 0.5,
            }}
          >
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              {e.export_format} — {e.status}
            </Typography>
            {e.file && (
              <a
                href={e.file.startsWith('http') ? e.file : `${getUrl()}${e.file}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: HEALTH_ACCENT, fontSize: 13 }}
              >
                Télécharger
              </a>
            )}
          </Box>
        ))}
      </Box>

      {/* Journal d'accès */}
      <Box sx={{ ...cardSx, mb: 2 }}>
        <SectionTitle>Journal d’accès</SectionTitle>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, mb: 1 }}>
          Transparence radicale : tout accès à vos données santé est tracé.
        </Typography>
        {logs.length === 0 ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>
            Aucun accès enregistré.
          </Typography>
        ) : (
          logs.map((l) => (
            <Typography
              key={l.id}
              sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, py: 0.25 }}
            >
              {new Date(l.accessed_at).toLocaleString('fr-FR')} —{' '}
              {l.action_label || l.action} {l.resource_type}
            </Typography>
          ))
        )}
      </Box>

      {/* Suppression du compte santé */}
      <Box sx={{ ...cardSx, border: '1px solid rgba(255,93,108,0.3)' }}>
        <Typography sx={{ color: '#ff5d6c', fontWeight: 700, mb: 0.5 }}>
          Supprimer mes données santé
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, mb: 1.5 }}>
          Suppression complète et définitive, avec un délai de grâce de 30
          jours pendant lequel vous pouvez annuler.
        </Typography>
        {deletion ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ color: '#ff5d6c', fontSize: 13 }}>
              Suppression prévue le{' '}
              {new Date(deletion.scheduled_purge_at).toLocaleDateString('fr-FR')}.
            </Typography>
            <Button
              size="small"
              onClick={cancelDeletion}
              sx={{ color: HEALTH_ACCENT, textTransform: 'none' }}
            >
              Annuler la suppression
            </Button>
          </Stack>
        ) : (
          <Button
            variant="outlined"
            size="small"
            onClick={requestDeletion}
            sx={{ color: '#ff5d6c', borderColor: 'rgba(255,93,108,0.5)' }}
          >
            Demander la suppression
          </Button>
        )}
      </Box>
    </Box>
  )
}
